/**
 * Realtime hooks over the shared `HotlineClient.realtime` Socket.IO client.
 *
 * - `useRealtimeConnection` — opens/closes the socket for the lifetime of the
 *   authenticated area and tracks connected state.
 * - `useTicketFeed` — keeps a live list of tickets: subscribes to
 *   `feedback.created` (prepend) and `feedback.updated` (replace in place),
 *   filtered to the active org scope. Returns the list + connection state.
 * - `useTicketEvents` — for a single open ticket: fires callbacks on updates and
 *   new comments for that session.
 *
 * All event payloads + names come from `@hotline/contracts` via client-core, so
 * there's no string drift.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import type {
  CommentCreatedPayload,
  FeedbackCreatedPayload,
  FeedbackUpdatedPayload,
  Ticket,
} from '@hotline/contracts';

import { useAuth } from '../auth/AuthContext.js';

/** Open the realtime socket while mounted; report connection status. */
export function useRealtimeConnection(): boolean {
  const { client, status } = useAuth();
  const [connected, setConnected] = useState(client.realtime.connected);

  useEffect(() => {
    if (status !== 'authenticated') return;
    const off = client.realtime.onConnectionChange(setConnected);
    client.realtime.connect();
    setConnected(client.realtime.connected);
    return () => {
      off();
      client.realtime.disconnect();
    };
  }, [client, status]);

  return connected;
}

export interface TicketFeedState {
  tickets: Ticket[];
  connected: boolean;
  /** Replace the whole list (e.g. after the initial REST load). */
  setTickets: (next: Ticket[]) => void;
  /** Merge one ticket (prepend if new, replace if existing). */
  upsert: (ticket: Ticket) => void;
}

/**
 * Maintain a live ticket list. Pass the active `orgId` so cross-org events are
 * ignored (meta-admin watching one org at a time). The caller seeds the initial
 * list from a REST fetch via `setTickets`.
 */
export function useTicketFeed(orgId: string | undefined): TicketFeedState {
  const { client, status } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const connected = useRealtimeConnection();
  const orgRef = useRef(orgId);
  orgRef.current = orgId;

  const upsert = useCallback((ticket: Ticket) => {
    setTickets((prev) => {
      const idx = prev.findIndex((t) => t.id === ticket.id);
      if (idx === -1) return [ticket, ...prev];
      const next = prev.slice();
      next[idx] = ticket;
      return next;
    });
  }, []);

  useEffect(() => {
    if (status !== 'authenticated') return;

    const matches = (eventOrgId: string): boolean =>
      // When no scope is selected (shouldn't happen for org-scoped views) accept
      // all; otherwise only the active org's events.
      !orgRef.current || eventOrgId === orgRef.current;

    const offCreated = client.realtime.on(
      'feedback.created',
      (p: FeedbackCreatedPayload) => {
        if (matches(p.organizationId)) upsert(p.ticket);
      },
    );
    const offUpdated = client.realtime.on(
      'feedback.updated',
      (p: FeedbackUpdatedPayload) => {
        if (matches(p.organizationId)) upsert(p.ticket);
      },
    );
    return () => {
      offCreated();
      offUpdated();
    };
  }, [client, status, upsert]);

  return { tickets, connected, setTickets, upsert };
}

/** Per-ticket realtime: invoke callbacks on updates + new comments for `sessionId`. */
export function useTicketEvents(
  sessionId: string,
  handlers: {
    onUpdate?: (ticket: Ticket) => void;
    onComment?: (payload: CommentCreatedPayload) => void;
  },
): void {
  const { client, status } = useAuth();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (status !== 'authenticated') return;
    const offUpdated = client.realtime.on(
      'feedback.updated',
      (p: FeedbackUpdatedPayload) => {
        if (p.ticket.id === sessionId) handlersRef.current.onUpdate?.(p.ticket);
      },
    );
    const offComment = client.realtime.on(
      'comment.created',
      (p: CommentCreatedPayload) => {
        if (p.sessionId === sessionId) handlersRef.current.onComment?.(p);
      },
    );
    return () => {
      offUpdated();
      offComment();
    };
  }, [client, status, sessionId]);
}
